import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  RadioGroup,
  Select,
  TableRow,
  TextField,
  makeStyles,
  Radio
} from '@material-ui/core';
import { ArrowDropDown } from '@material-ui/icons';
import React from 'react';
import {
  useGetPushDestinations,
  useGetCampaignList,
  usePushSegment,
  useGetCampaignTerm
} from '../../../hooks/useSegmentPush';
import {
  HeaderCell,
  SearchBar,
  SecondaryAlert,
  SecondaryButton,
  Table,
  TableBody,
  TableHead,
  TertiaryButton,
  PrimaryButton
} from 'kagr-ui';
import { TableSortState } from '../../../models/tableSortState';
import * as styles from '../segments.module.scss';
import * as actionStyles from '../create-segment.module.scss';
import { PushDestination } from '../../../models/segments';
import Pagination from '../../pagination/pagination';
import clsx from 'clsx';
import { colors, neutral } from '../../../theme/colors';
import { RouteComponentProps, useParams, withRouter } from 'react-router';
import Paths from '../../../models/enums/paths';
import useTimedAlerts from '../../../hooks/useTimedAlerts';
import PushSegmentBreadcrumbs from '../breadcrumbs/push-segment-breadcrumbs';
import { useClients } from '../../../hooks/useClients';
import { AppContext } from '../../../shared/app-context';
import { datadogRum } from '@datadog/browser-rum';
import { useSegment } from '../../../hooks/useSegment';

const useStyles = makeStyles((theme) => ({
  dialog: {
    [theme.breakpoints.up('sm')]: {
      '& .MuiDialog-paperWidthSm': {
        width: '95rem',
        maxWidth: '95rem'
      }
    }
  },
  dialogContent: {
    padding: 0
  },
  dialogActions: {
    justifyContent: 'space-between',
    height: 57,
    padding: '0 24px'
  },
  searchBar: {
    width: 180
  },
  tableRow: {
    cursor: 'pointer'
  },
  tableHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 30px 0'
  },
  pushHistoryLabel: {
    color: '#074F86',
    textTransform: 'none',
    textDecoration: 'underline',
    fontSize: '12px'
  },
  pushPagination: {
    display: 'flex',
    justifyContent: 'center'
  },
  sourceSystem: {
    fontWeight: 'normal'
  },
  pushMessage: {
    position: 'absolute',
    top: '20px',
    left: '30%'
  },
  inputs: {
    margin: '15px 0',
    '& .MuiInputLabel-asterisk': {
      color: colors.rustRed
    }
  },
  attributeSelect: {
    width: '360px'
  },
  header: {
    display: 'flex',
    marginBottom: '8px',
    borderBottom: '1px solid #979797'
  },
  title: {
    color: neutral[600],
    fontSize: 18,
    fontWeight: 600,
    margin: '8px 0',
    lineHeight: '1em'
  },
  wizardMain: {
    padding: '25px',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '90%'
  },
  wizardContainer: {
    padding: '25px',
    display: 'flex',
    flexDirection: 'column',
    height: '80%'
  },
  selectionContainer: {
    padding: '20px 0',
    display: 'flex',
    flexDirection: 'column'
  },
  selectionTitle: {
    fontSize: '16px',
    fontWeight: 500
  },
  campaignInput: {
    '& .MuiOutlinedInput-input': {
      paddingTop: '10.5px',
      paddingBottom: '10.5px',
      cursor: 'pointer'
    }
  },
  emptyInput: {
    '& .MuiInputLabel-outlined': {
      transform: 'translate(14px, 12px) scale(1)'
    }
  }
}));

enum PushType {
  Single = 1,
  Automatic = 2
}

interface SelectedCampaign {
  id: string;
  name: string;
}

const PushSegment = ({ history }: RouteComponentProps) => {
  const { segmentId } = useParams<{ segmentId: string }>();
  const { segmentQuery } = useSegment(segmentId);

  const [selectedDestination, setSelectedDestination] = React.useState<null | PushDestination>(null);
  const [selectedCampaign, setSelectedCampaign] = React.useState<SelectedCampaign>(null);
  const [campaignMenuOpen, setCampaignMenuOpen] = React.useState<boolean>(false);
  const [pushType, setPushType] = React.useState<PushType | ''>('');
  const [campaignTerm, setCampaignTerm] = React.useState<string>('Campaign');
  const classes = useStyles();
  const { errorMessage, setErrorMessage } = useTimedAlerts();
  const pushSelectedCampaign = usePushSegment();
  const clientsQuery = useClients();
  const { state } = React.useContext(AppContext);
  const campaignTermQuery = useGetCampaignTerm(
    selectedDestination?.pushDestinationId.toString(),
    state?.clientId.toString()
  );

  const userName = React.useRef<string>('');
  const segmentName = React.useRef<string>('');
  const segmentShare = React.useRef<string>('');

  React.useEffect(() => {
    if (segmentQuery.data) {
      segmentName.current = segmentQuery.data.name;
      segmentShare.current = segmentQuery.data.isShared ? 'Share' : 'Do not share';
    }
  }, [segmentQuery.data]);

  const currentClient = React.useMemo(() => {
    userName.current = `${state.firstName} ${state.lastName}`;
    if (clientsQuery.data && state.clientId) {
      return clientsQuery.data.results.find((client) => client.clientId === state.clientId);
    }
  }, [state.clientId, clientsQuery.data]);

  const pushDestinations = useGetPushDestinations();

  const selectDestionation = (destinationId: number) => {
    const newDestination = pushDestinations.data?.find((pd) => pd.pushDestinationId === destinationId);
    setSelectedDestination(newDestination ? newDestination : null);
    setSelectedCampaign(null);
  };

  React.useEffect(() => {
    if (campaignTermQuery.data) {
      setCampaignTerm(campaignTermQuery.data);
    }
  }, [campaignTermQuery.data]);

  const createPush = () => {
    pushSelectedCampaign.mutate(
      {
        pushDestinationId: selectedDestination.pushDestinationId,
        campaignId: selectedCampaign.id,
        campaignName: selectedCampaign.name,
        segmentId: segmentId,
        isOneTime: pushType !== PushType.Automatic
      },
      {
        onSuccess() {
          datadogRum.addUserAction('push-segment', {
            user_action_details: {
              segment_id: segmentId,
              segment_name: segmentName.current,
              is_segment_shared: segmentShare.current,
              user_name: userName.current,
              system: selectedDestination.displayName,
              campaignId: selectedCampaign.id
            }
          });
          history.push(Paths.SegmentPushes.replace(':segmentId', segmentId));
        },
        onError() {
          setErrorMessage('There was an error creating the push');
        }
      }
    );
  };

  const invalidPush = (): boolean => {
    const validPushType = !currentClient.enableAutomaticPushes || !!pushType;
    return !selectedDestination || !selectedCampaign || !validPushType;
  };

  const getCampaignLabel = (): string => {
    const regex = new RegExp(/^[aeiou]/i);
    return regex.test(campaignTerm) ? `Select an ${campaignTerm}` : `Select a ${campaignTerm}`;
  };

  return (
    <React.Fragment>
      {errorMessage && (
        <div className={classes.pushMessage}>
          <SecondaryAlert type="error" message={errorMessage} />
        </div>
      )}
      <div className={styles.root}>
        <PushSegmentBreadcrumbs currentPage={'Create New Push'} />
        <div className={classes.wizardMain}>
          <div className={classes.header}>
            <h2 className={classes.title}>Push Configuration</h2>
          </div>
          <section className={classes.wizardContainer}>
            <div className={classes.selectionContainer}>
              <div className={classes.selectionTitle}>Which system do you want to push to?</div>
              <FormControl
                size="small"
                variant="outlined"
                className={clsx(classes.inputs, classes.attributeSelect)}
                required={true}
              >
                <InputLabel id="system-selection">Select a System</InputLabel>
                <Select
                  labelId="system-selection"
                  value={selectedDestination ? String(selectedDestination.pushDestinationId) : ''}
                  onChange={(event: any) => selectDestionation(event.target.value)}
                >
                  {pushDestinations.data?.map((destination) => (
                    <MenuItem
                      value={destination.pushDestinationId}
                      key={`Destination-Option-${destination.pushDestinationId}`}
                    >
                      {destination.displayName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className={classes.selectionContainer}>
              <div className={classes.selectionTitle}>Which {campaignTerm} do you want to push to?</div>
              <FormControl
                size="small"
                variant="outlined"
                className={clsx(classes.inputs, classes.attributeSelect)}
                required={true}
              >
                <TextField
                  required
                  id="outlined-read-only-input"
                  label={getCampaignLabel()}
                  value={selectedCampaign?.name ?? ''}
                  onClick={() => {
                    if (selectedDestination) {
                      setCampaignMenuOpen(true);
                    }
                  }}
                  InputProps={{
                    readOnly: true,
                    endAdornment: <ArrowDropDown />
                  }}
                  variant="outlined"
                  disabled={!selectedDestination}
                  className={clsx(classes.campaignInput, selectedCampaign ? '' : classes.emptyInput)}
                />
                <CampaignMenu
                  openDialog={campaignMenuOpen}
                  destinationId={selectedDestination?.pushDestinationId}
                  destinationTitle={selectedDestination?.displayName}
                  cancelPush={() => setCampaignMenuOpen(false)}
                  pushToCampaign={(newSelectedCampaign) => {
                    let campaignId = '';
                    let campaignName = '';
                    // The keys coming back from the Integration API aren't always the same casing but the keys we are looking for are the same
                    // Doing this step to find the information we need to send when creating a new push
                    Object.keys(newSelectedCampaign).forEach((key) => {
                      if (key.toLocaleLowerCase() === 'id') {
                        campaignId = newSelectedCampaign[key];
                      }
                      if (key.toLocaleLowerCase() === 'name') {
                        campaignName = newSelectedCampaign[key];
                      }
                    });
                    setSelectedCampaign({
                      id: campaignId,
                      name: campaignName
                    });
                    setCampaignMenuOpen(false);
                  }}
                />
              </FormControl>
            </div>
            {currentClient?.enableAutomaticPushes && (
              <div>
                <div className={classes.selectionTitle}>How often do you want to push?</div>
                <RadioGroup
                  aria-label="push-type"
                  name="push-type"
                  value={pushType}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setPushType(Number(event.target.value));
                  }}
                >
                  <FormControlLabel value={PushType.Single} control={<Radio />} label="One time" />
                  <FormControlLabel
                    value={PushType.Automatic}
                    control={<Radio />}
                    label="Every time warehouse data refreshes"
                  />
                </RadioGroup>
              </div>
            )}
          </section>
          <div className={actionStyles.actionFooter}>
            <TertiaryButton
              component="button"
              text="Cancel"
              title="Cancel"
              handleClick={() => {
                history.push(Paths.SegmentPushes.replace(':segmentId', segmentId));
              }}
            />
            <PrimaryButton text="Create Push" title="Create Push" handleClick={createPush} deactive={invalidPush()} />
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

type CampaignMenuProps = {
  openDialog: boolean;
  destinationId: number;
  destinationTitle: string;
  cancelPush: () => void;
  pushToCampaign: (campaignId: any) => void;
};

interface PushCampaignName {
  title: string;
  orderField: string;
}

const CampaignMenu = ({
  openDialog,
  destinationId,
  destinationTitle,
  cancelPush,
  pushToCampaign
}: CampaignMenuProps) => {
  const overrides = useStyles();
  const [tableSort, setTableSort] = React.useState<TableSortState>({
    page: 1,
    pageSize: 15,
    orderColumn: '',
    orderDirection: 'desc'
  });

  const [selectedCampaign, setSelectedCampaign] = React.useState<string>(null);
  const [searchValue, setSearchValue] = React.useState<string>('');
  const [activeSearchTerm, setActiveSearchTerm] = React.useState<string>('');
  const [pushCampaignNames, setPushCampaignNames] = React.useState<PushCampaignName[]>([]);
  const idKey = React.useRef<string>('Id');

  const campaignList = useGetCampaignList(destinationId, tableSort, activeSearchTerm);

  function reorderHandler(column: string, direction: 'asc' | 'desc') {
    setTableSort((state) => ({ ...state, orderColumn: column, orderDirection: direction }));
  }

  React.useEffect(() => {
    if (campaignList.data) {
      const newPushCampaignNames = [];
      campaignList.data.propertyNames?.forEach((propertyName, index) => {
        // Getting the id key that will be used to make sure we get the correct campaign
        // Id key coming back from the Integration API might not always be the same case, so doing this to make sure we capture the id key
        if (propertyName.toLocaleLowerCase() === 'id') {
          idKey.current = propertyName;
        }

        newPushCampaignNames.push({
          title: campaignList.data.displayNames[index],
          orderField: propertyName
        });
      });
      setPushCampaignNames(newPushCampaignNames);
    }
  }, [campaignList.data]);

  return (
    <Dialog open={openDialog} className={overrides.dialog}>
      <DialogTitle disableTypography>Select Campaign</DialogTitle>
      <DialogContent dividers className={overrides.dialogContent}>
        {campaignList.isLoading && !campaignList.data && (
          <div className={styles.loading}>
            <CircularProgress />
          </div>
        )}
        {campaignList.isError && (
          <div className={styles.error}>
            <SecondaryAlert type="error" message={`Failed to fetch results: ${campaignList.error.message}`} />
          </div>
        )}
        {campaignList.data && (
          <>
            <div className={overrides.tableHeader}>
              <div className={styles.titleContainer}>
                <div className={styles.tableTitle}>
                  <span className={overrides.sourceSystem}>SOURCE SYSTEM:</span> {destinationTitle}
                </div>
              </div>
              <SearchBar
                value={searchValue}
                updateValue={setSearchValue}
                submitHandler={() => setActiveSearchTerm(searchValue)}
                placeholder="Search by Keyword"
                allowSubmitButton={false}
                className={overrides.searchBar}
              />
            </div>
            <Table className={styles.table}>
              <TableHead>
                {pushCampaignNames.map((header) => (
                  <HeaderCell
                    key={header.title}
                    title={header.title}
                    orderField={header.orderField ? header.orderField : null}
                    orderColumn={header.orderField ? tableSort.orderColumn : null}
                    orderDirection={header.orderField ? tableSort.orderDirection : null}
                    reorderHandler={header.orderField ? reorderHandler : null}
                  />
                ))}
              </TableHead>
              <TableBody>
                {campaignList.data.items.totalItems === 0 && (
                  <tr>
                    <td colSpan={pushCampaignNames.length}>No results found</td>
                  </tr>
                )}
                {campaignList.data.items.results.map((data, rowIndex) => (
                  <TableRow
                    hover
                    key={rowIndex}
                    onClick={() => setSelectedCampaign(data[idKey.current])}
                    className={overrides.tableRow}
                    selected={data[idKey.current] === selectedCampaign}
                  >
                    {campaignList.data.propertyNames.map((prop, dataIndex) => (
                      <td key={`${rowIndex} - ${dataIndex} - ${data[prop]}`}>{data[prop]}</td>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
        {campaignList.data && (
          <div className={`${styles.paginationFooter} ${overrides.pushPagination}`}>
            <Pagination
              totalPages={Math.ceil(campaignList.data.items.totalItems / tableSort.pageSize)}
              pageSize={tableSort.pageSize}
              currentPage={tableSort.page}
              disabled={campaignList.isPreviousData && campaignList.isFetching}
              handlePageChange={(page) => setTableSort((state) => ({ ...state, page }))}
              handlePageSizeChange={(pageSize) =>
                setTableSort((state) => ({
                  ...state,
                  page: 1,
                  pageSize
                }))
              }
            />
          </div>
        )}
      </DialogContent>
      <DialogActions className={overrides.dialogActions}>
        <TertiaryButton text="Cancel" title="Cancel" component="button" handleClick={cancelPush} />
        <SecondaryButton
          text="Push"
          title="Push"
          disabled={!selectedCampaign}
          handleClick={() => {
            const newCampaign = campaignList.data.items.results.find((c) => c[idKey.current] === selectedCampaign);
            pushToCampaign(newCampaign);
          }}
        />
      </DialogActions>
    </Dialog>
  );
};

export default withRouter(PushSegment);
